/**
 * useAutoSave Hook
 * Provides auto-save functionality with change tracking, debouncing, and status reporting
 *
 * Correctness rules (each one was a lost-edit bug):
 *
 *   - An edit made WHILE a save is in flight is never lost. Every markDirty()
 *     bumps a revision; a save records the revision it started from and, when it
 *     finishes, only that revision counts as persisted. Anything newer stays
 *     dirty and is saved afterwards. (It used to clear the dirty flag when the
 *     save finished, so the second of two quick undos was silently dropped.)
 *   - One save at a time. A request that arrives while a save is in flight is
 *     coalesced into ONE follow-up that runs when the current save finishes —
 *     no overlapping writes of the same flow, no save storm.
 *   - A save always serialises the state of the latest COMMITTED render: the
 *     save function is refreshed in a layout effect, not a passive one.
 *   - A failed save leaves the flow dirty; it is retried by the next edit, the
 *     interval, visibility/unload, or an explicit triggerSave — never in a loop.
 *   - Switching flows (`flowKey`) starts a fresh session. Unsaved edits of the
 *     previous flow are flushed with the previous flow's own save function (its
 *     own nodes and id), never written into the new flow, and the new flow
 *     does not inherit the old one's dirty state. Unmounting flushes too.
 */

import { useEffect, useLayoutEffect, useRef, useState, useCallback } from 'react';
import logger from '../utils/logger';

export interface AutoSaveContext {
  /**
   * False once the session this save belongs to has been left (another flow
   * was opened, or the editor unmounted before the save ran). A save for a
   * left session must not adopt ids or otherwise touch the editor's state.
   */
  isCurrent: () => boolean;
}

export interface UseAutoSaveOptions {
  enabled: boolean;
  saveInterval?: number; // milliseconds between auto-saves (default: 2 minutes)
  debounceDelay?: number; // milliseconds to wait after last change before saving (default: 5 seconds)
  onSave: (ctx: AutoSaveContext) => Promise<void>;
  /**
   * Identity of the document being edited. Changing it starts a new session
   * (see the file header). Leave undefined for a single-document editor.
   */
  flowKey?: unknown;
  /**
   * Called (with the render-time closure of the session being LEFT) when that
   * session's unsaved edits will be flushed. The promise settles once the
   * flush is done. Lets the caller register the write in pendingFlowSaves
   * immediately — before it waits behind an in-flight save — so reopening
   * the same flow waits for it instead of loading a stale row.
   */
  onFlushScheduled?: (flush: Promise<void>) => void;
}

/** Opaque "what the editor had changed by now" — see snapshotRevision(). */
export interface SaveRevision {
  readonly session: unknown;
  readonly revision: number;
}

export type AutoSaveStatus = 'idle' | 'saving' | 'saved' | 'error';

export interface UseAutoSaveReturn {
  isDirty: boolean;
  status: AutoSaveStatus;
  lastSaved: Date | null;
  triggerSave: (force?: boolean) => Promise<void>;
  markDirty: () => void;
  markClean: () => void;
  /** Capture the current revision BEFORE an external (manual) save serialises the graph. */
  snapshotRevision: () => SaveRevision;
  /**
   * An external save of the state at `rev` succeeded. Only edits up to that
   * revision count as saved; anything made while it ran stays dirty.
   */
  markSavedUpTo: (rev: SaveRevision) => void;
}

/** Everything that belongs to ONE open document. Replaced when `flowKey` changes. */
interface Session {
  /** Bumped by every markDirty(). */
  revision: number;
  /** Highest revision known to be persisted. dirty === revision > saved. */
  saved: number;
  /** The running save loop, if any. */
  drain: Promise<void> | null;
  /** A save was requested while one was in flight. */
  followUp: boolean;
  /** ...and that request was forced (save even if nothing is marked dirty). */
  followUpForced: boolean;
  /** False once the editor moved to another document or unmounted. */
  active: boolean;
}

const newSession = (): Session => ({
  revision: 0,
  saved: 0,
  drain: null,
  followUp: false,
  followUpForced: false,
  active: true,
});

const isSessionDirty = (s: Session) => s.revision > s.saved;

/**
 * Auto-save hook that tracks changes and periodically saves with debouncing
 */
export function useAutoSave(options: UseAutoSaveOptions): UseAutoSaveReturn {
  const {
    enabled,
    saveInterval = 120000, // 2 minutes default
    debounceDelay = 5000,   // 5 seconds default
    onSave,
    flowKey,
    onFlushScheduled,
  } = options;

  const [isDirty, setIsDirty] = useState(false);
  const [status, setStatus] = useState<AutoSaveStatus>('idle');
  const [lastSaved, setLastSaved] = useState<Date | null>(null);

  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const intervalTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const statusTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sessionRef = useRef<Session>(newSession());
  const onSaveRef = useRef(onSave); // always the latest committed render's save
  const onFlushScheduledRef = useRef(onFlushScheduled);
  const enabledRef = useRef(enabled); // Use ref for enabled check in callbacks
  const debounceDelayRef = useRef(debounceDelay); // Use ref for debounce delay
  const flowKeyRef = useRef(flowKey);

  // Keep enabled ref updated
  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  // Keep debounceDelay ref updated
  useEffect(() => {
    debounceDelayRef.current = debounceDelay;
  }, [debounceDelay]);

  const clearDebounce = () => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
  };

  /** Mirror the session's dirty state into React state (current session only). */
  const syncDirty = useCallback((s: Session) => {
    if (s === sessionRef.current) setIsDirty(isSessionDirty(s));
  }, []);

  const setStatusFor = useCallback((s: Session, next: AutoSaveStatus, resetAfterMs?: number) => {
    if (s !== sessionRef.current) return;
    if (statusTimerRef.current) {
      clearTimeout(statusTimerRef.current);
      statusTimerRef.current = null;
    }
    setStatus(next);
    if (resetAfterMs !== undefined) {
      statusTimerRef.current = setTimeout(() => {
        statusTimerRef.current = null;
        setStatus('idle');
      }, resetAfterMs);
    }
  }, []);

  /**
   * Request a save for the current session. Runs now if nothing is in flight,
   * otherwise coalesces into one follow-up after the in-flight save. The
   * returned promise settles once the save that covers this request has
   * finished (it never rejects; failures are reported through `status`).
   */
  const requestSave = useCallback((force: boolean): Promise<void> => {
    const s = sessionRef.current;
    if (s.drain) {
      s.followUp = true;
      if (force) s.followUpForced = true;
      return s.drain;
    }
    if (!force && !isSessionDirty(s)) return Promise.resolve();

    const drain = (async () => {
      let attempt = true;
      while (attempt && s.active) {
        const rev = s.revision;
        s.followUp = false;
        s.followUpForced = false;
        setStatusFor(s, 'saving');
        let failed = false;
        try {
          await onSaveRef.current({ isCurrent: () => s.active });
          if (rev > s.saved) s.saved = rev;
          if (s === sessionRef.current) setLastSaved(new Date());
          logger.debug('[AutoSave] Auto-save completed (revision', rev, ')');
        } catch (error) {
          failed = true;
          logger.error('[AutoSave] Auto-save failed:', error);
        }
        syncDirty(s);

        // Another request arrived during the save. It needs a save of its own
        // if it was forced, or if an edit landed after this save's snapshot.
        // A failure is not retried here — only a new request retries it.
        const needsMore = s.followUpForced || (s.followUp && isSessionDirty(s));
        attempt = needsMore;
        if (!needsMore) {
          if (failed) setStatusFor(s, 'error', 5000);
          else setStatusFor(s, 'saved', 3000);
        }
      }
    })().finally(() => {
      if (s.drain === drain) s.drain = null;
    });
    s.drain = drain;
    return drain;
  }, [setStatusFor, syncDirty]);

  /**
   * Trigger a save (exposed to external callers)
   * @param force - If true, save even if not marked dirty and even if auto-save is disabled (useful for navigation/name changes)
   */
  const triggerSave = useCallback(async (force: boolean = false) => {
    // If forced, skip the enabled check (allows saving on navigation even with auto-save off)
    if (!force && !enabledRef.current) {
      return;
    }

    // Skip if not dirty and not forced
    if (!isSessionDirty(sessionRef.current) && !force) {
      return;
    }

    // This save supersedes the pending debounce.
    clearDebounce();

    await requestSave(force);
  }, [requestSave]);

  /**
   * Mark content as dirty (changed)
   */
  const markDirty = useCallback(() => {
    if (!enabledRef.current) {
      logger.debug('[AutoSave] markDirty called but auto-save is disabled');
      return;
    }

    const s = sessionRef.current;
    s.revision += 1;
    setIsDirty(true);

    // Restart the debounce: a burst of edits is one save.
    clearDebounce();
    debounceTimerRef.current = setTimeout(() => {
      debounceTimerRef.current = null;
      logger.debug('[AutoSave] Debounce timer fired');
      void requestSave(false);
    }, debounceDelayRef.current);
  }, [requestSave]);

  /**
   * Mark content as clean (saved by some other path, e.g. an explicit Save)
   */
  const markClean = useCallback(() => {
    const s = sessionRef.current;
    s.saved = s.revision;
    setIsDirty(false);
    clearDebounce();
  }, []);

  const snapshotRevision = useCallback((): SaveRevision => {
    const s = sessionRef.current;
    return { session: s, revision: s.revision };
  }, []);

  const markSavedUpTo = useCallback((rev: SaveRevision) => {
    const s = rev.session as Session;
    if (rev.revision > s.saved) s.saved = rev.revision;
    syncDirty(s);
    // Nothing left to save: the pending debounce would write the same graph.
    if (s === sessionRef.current && !isSessionDirty(s)) clearDebounce();
  }, [syncDirty]);

  /**
   * Leave the current session: the old document's unsaved edits are flushed
   * with the old document's save function, after anything already in flight.
   */
  const leaveSession = useCallback((reason: string) => {
    const old = sessionRef.current;
    old.active = false;
    clearDebounce();
    if (!isSessionDirty(old) || !enabledRef.current) return;
    const saveOld = onSaveRef.current; // captured BEFORE the ref moves on
    const announce = onFlushScheduledRef.current;
    const inFlight = old.drain;
    logger.debug('[AutoSave] Flushing unsaved edits on', reason);
    const flush = (async () => {
      if (inFlight) await inFlight;
      // The in-flight save may already have covered every edit.
      if (!isSessionDirty(old)) return;
      const rev = old.revision;
      try {
        await saveOld({ isCurrent: () => false });
        if (rev > old.saved) old.saved = rev;
      } catch (error) {
        logger.error('[AutoSave] Flush of the previous flow failed:', error);
      }
    })();
    announce?.(flush);
  }, []);

  // A different document was opened. Runs BEFORE onSaveRef is refreshed below
  // (layout effects run in declaration order), so onSaveRef still holds the
  // previous render's save — the old flow's nodes paired with the old flow's id.
  useLayoutEffect(() => {
    if (Object.is(flowKeyRef.current, flowKey)) return;
    flowKeyRef.current = flowKey;
    leaveSession('flow switch');
    sessionRef.current = newSession();
    setIsDirty(false);
  }, [flowKey, leaveSession]);

  // The latest committed render's save. A layout effect, so no timer can run
  // between a commit and this update and serialise an older graph.
  useLayoutEffect(() => {
    onSaveRef.current = onSave;
    onFlushScheduledRef.current = onFlushScheduled;
  }, [onSave, onFlushScheduled]);

  /**
   * Set up interval-based auto-save
   */
  useEffect(() => {
    if (!enabled) {
      // Clear timers if auto-save is disabled
      if (intervalTimerRef.current) {
        clearInterval(intervalTimerRef.current);
        intervalTimerRef.current = null;
      }
      return;
    }

    // Set up interval timer
    intervalTimerRef.current = setInterval(() => {
      if (isSessionDirty(sessionRef.current)) {
        logger.debug('Interval-based auto-save triggered');
        void requestSave(false);
      }
    }, saveInterval);

    return () => {
      if (intervalTimerRef.current) {
        clearInterval(intervalTimerRef.current);
      }
    };
  }, [enabled, saveInterval, requestSave]);

  /**
   * Handle page visibility changes and beforeunload
   * Save when tab becomes hidden or user tries to close/navigate away
   */
  useEffect(() => {
    if (!enabled) {
      return;
    }

    const handleVisibilityChange = () => {
      if (document.hidden && isSessionDirty(sessionRef.current)) {
        logger.debug('Tab hidden, triggering auto-save');
        void requestSave(false);
      }
    };

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isSessionDirty(sessionRef.current)) {
        // Trigger save (non-blocking); coalesces behind an in-flight save.
        void requestSave(false);

        // Show browser warning if there are unsaved changes
        e.preventDefault();
        e.returnValue = '';
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [enabled, requestSave]);

  // A save that outlives this component — navigating home backgrounds one —
  // is guarded against tab close at module scope instead, since any listener
  // registered here is removed by the very unmount it would need to survive.
  // See src/services/pendingFlowSaves.ts.

  /**
   * On unmount: stop the timers, and flush edits that were still waiting for
   * their debounce instead of dropping them.
   */
  useEffect(() => {
    // StrictMode runs this cleanup and then this setup again on the SAME refs:
    // the session is still the live one, so take it back rather than leave
    // every later save refused as belonging to a left session.
    sessionRef.current.active = true;
    return () => {
      if (intervalTimerRef.current) {
        clearInterval(intervalTimerRef.current);
      }
      if (statusTimerRef.current) {
        clearTimeout(statusTimerRef.current);
      }
      leaveSession('unmount');
    };
  }, [leaveSession]);

  return {
    isDirty,
    status,
    lastSaved,
    triggerSave,
    markDirty,
    markClean,
    snapshotRevision,
    markSavedUpTo,
  };
}
